import React from "react";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import DataTable from "../../components/dataTable";
import { GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { LoadingPanel } from "../../components/loadingPanel";
import { columnMenuProps } from "../../components/customColumnMenu";
import { getTenantCookieData, getUserCredential } from "../../lib/cookies";
import { transformDateTime } from "src/lib/date-time.util";
import { HeaderPage } from "../base/headerPage";
import { getfilterDataString } from "../../utils/utils";
import { DEFAULT_SORT } from "../../constants/grid";
import useAPI from "../filterQuery/service.js";

const ModalTitle = () => {
  return <span>Saved Query List</span>;
};

// Ordering the list loads with (action date, newest first). Used as the fallback
// when the user hasn't applied their own sort.
const INITIAL_SORT = { number: 7, order: "DESC" };

const SavedQueryGrid = ({ visible, onClose, onSelect, onSelectAndApply, dbObjName, module }) => {
  const defaultPageSize = getTenantCookieData("record_per_page");
  const cookiesData = getUserCredential();

  const gridHeight = Math.floor(window.innerHeight * 0.9) - 152;

  const [stageChange, setStateChange] = React.useState("DEFAULT");
  const [modal, setModal] = React.useState(true);
  const [pageData, setPage] = React.useState({
    skip: 0,
    take: defaultPageSize.recordTake,
  });
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [filterField, setFilterField] = React.useState({ logic: "and", filters: [] });
  const [sortField, setSortField] = React.useState([]);
  const [resetSort] = React.useState(false);
  const [filterData, setFilterData] = React.useState({
    isSort: false,
    isFilter: false,
    data: "",
    in_sort_number: DEFAULT_SORT.number,
    sort_order: DEFAULT_SORT.order,
  });

  // useAPI returns new objects every render — use a stable ref for Action
  const [list, Action] = useAPI();
  const { data, columns, initialLoading, isAPICalling, dataCount, noDataFound, setAPICallingState } = list;

  // Stable ref — keeps Action current without causing re-renders or infinite loops
  const ActionRef = React.useRef(Action);
  React.useEffect(() => {
    ActionRef.current = Action;
  });

  // Base params reused across all API calls (stable — only changes when dbObjName/module change)
  const baseParams = React.useMemo(
    () => ({
      in_tenant_id: cookiesData?.out_tenant_id || "",
      in_user_id: cookiesData?.out_user_id || "",
      in_db_obj_code: dbObjName,
      module,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dbObjName, module]
  );

  const getFilterList = React.useCallback(() => {
    ActionRef.current.GetFilterQUery({
      ...baseParams,
      in_filter: null,
      in_sort_number: INITIAL_SORT.number,
      in_sort_order: INITIAL_SORT.order,
      in_page_row_offset: 0,
      in_page_rows: defaultPageSize.recordTake,
    });
  }, [baseParams, defaultPageSize.recordTake]);

  // Fetch list only when the grid becomes visible
  React.useEffect(() => {
    if (!visible) return;
    getFilterList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const headerConfig = React.useMemo(() => ({ id: "filtered_query" }), []);

  const handleStageChange = React.useCallback((e) => {
    setStateChange(e.state);
    if (e.state === "MINIMIZED") {
      setModal(false);
    } else if (e.state === "DEFAULT") {
      setModal(true);
    }
  }, []);

  const onClosePopup = React.useCallback(() => {
    onClose?.();
  }, [onClose]);

  const ActionCell = React.useCallback(
    (props) => {
      const { dataItem } = props;
      return (
        <td title="Show query results" className="text-center">
          <span
            className="pointer-class k-icon search-query-icon"
            title="Show query results"
            onClick={() => onSelectAndApply?.(dataItem)}
          ></span>
        </td>
      );
    },
    [onSelectAndApply]
  );

  const BooleanCell = React.useCallback((props) => {
    const { ariaColumnIndex, columnIndex, dataItem, field } = props;
    const val = Number(dataItem?.[field]) ? "true" : "false";
    return (
      <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
        <span title={val}>{val}</span>
      </td>
    );
  }, []);

  const DateCell = React.useCallback((props) => {
    const { ariaColumnIndex, columnIndex, dataItem, field } = props;
    const raw = dataItem?.[field];
    const val =
      raw && raw !== "0000-00-00 00:00:00"
        ? transformDateTime({ date: raw, format: "MM/DD/YYYY", includeTime: true })
        : "";
    return (
      <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
        <span title={val}>{val}</span>
      </td>
    );
  }, []);

  const StringCell = React.useCallback((props) => {
    const { ariaColumnIndex, columnIndex, dataItem, field } = props;
    const val = dataItem?.[field || ""];
    return (
      <td aria-colindex={ariaColumnIndex} data-grid-col-index={columnIndex}>
        <span title={val}>{val}</span>
      </td>
    );
  }, []);

  const LinkCell = React.useCallback(
    (props) => {
      const { dataItem } = props;
      const onClick = () => onSelect?.(dataItem);
      return (
        <td title="Edit/View Query">
          {dataItem?.is_favorite ? (
            <Tooltip openDelay={100} position="top" anchorElement="target" className="text-center">
              <span className="link-class" onClick={onClick} title="Edit/View Query">
                <i className="fas fa-star"></i> {dataItem?.filter_name}
              </span>{" "}
            </Tooltip>
          ) : (
            <span className="link-class" onClick={onClick}>
              {dataItem?.filter_name}
            </span>
          )}
        </td>
      );
    },
    [onSelect]
  );

  const cellType = React.useCallback(
    (type) => {
      switch (type) {
        case "date":
          return DateCell;
        case "boolean":
          return BooleanCell;
        default:
          return StringCell;
      }
    },
    [BooleanCell, DateCell, StringCell]
  );

  // columns comes from list state — populated by GetFilterQUery via out_column_xml
  const UseCol = columns.map((column, idx) => {
    return column.show && column.title !== "" ? (
      <Column
        {...columnMenuProps(column)}
        key={idx}
        field={column.field}
        title={<span title={column.title}>{column.title}</span>}
        cell={
          column.field === "filter_name" ? LinkCell : column.field === "expand" ? ActionCell : cellType(column.type)
        }
        format={column.format}
        filterable={column.field === "expand" ? false : true}
        sortable={column.field === "expand" ? false : true}
        width={column.field === "expand" ? 60 : "150px"}
        reorderable={column.field === "expand" ? false : true}
        resizable={column.field === "expand" ? false : true}
      />
    ) : (
      false
    );
  });

  const handleColumnMenu = React.useCallback(
    async (columnMenuData, isFilter, isSort, filterObj) => {
      if (isSort) setSortField(columnMenuData);
      else setFilterField(filterObj);

      let index = 1;
      if (data.length > 0 && columnMenuData[0] !== undefined) {
        const obj = Object.keys(data[0]);
        index = obj.findIndex((x) => x === columnMenuData[0].field) + 1;
      }

      const filterString = isFilter ? getfilterDataString(columnMenuData, columns) : filterData.data;

      const hasFilter = filterString !== "";
      let sortDir = filterData.isSort && columnMenuData.length ? filterData.sort_order : DEFAULT_SORT.order;
      let sortNumber = filterData.isSort && columnMenuData.length ? filterData.in_sort_number : DEFAULT_SORT.number;
      const hasSort = columnMenuData[0] !== undefined && columnMenuData[0].dir !== undefined;
      if (hasSort) {
        sortDir = columnMenuData[0].dir.toUpperCase();
        sortNumber = index;
      }

      setFilterData((prev) => ({
        ...prev,
        isFilter: hasFilter,
        isSort: hasSort,
        data: filterString,
        in_sort_number: sortNumber,
        sort_order: sortDir,
      }));

      setAPICallingState(true);
      ActionRef.current.GetFilterQUery({
        ...baseParams,
        in_filter: hasFilter ? filterString : "",
        in_sort_number: sortNumber,
        in_sort_order: sortDir,
        in_page_row_offset: pageData.skip,
        in_page_rows: pageData.take,
      });
    },
    [baseParams, columns, data, filterData, pageData, setAPICallingState]
  );

  const handlePageChange = React.useCallback(
    async (event) => {
      const next = event.page;
      setPage({ skip: next.skip, take: next.take });
      setAPICallingState(true);
      ActionRef.current.GetFilterQUery({
        ...baseParams,
        in_filter: filterData.isFilter ? filterData.data : "",
        in_sort_number: filterData.isSort ? filterData.in_sort_number : DEFAULT_SORT.number,
        in_sort_order: filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order,
        in_page_row_offset: next.skip,
        in_page_rows: next.take,
      });
    },
    [baseParams, filterData, setAPICallingState]
  );

  // Re-runs the current query rather than resetting it, so the user's column
  // filter, sort and page stays consistent across a refresh.
  const onRefreshClick = React.useCallback(() => {
    setAPICallingState(true);
    ActionRef.current.GetFilterQUery({
      ...baseParams,
      in_filter: filterData.isFilter ? filterData.data : "",
      in_sort_number: filterData.isSort ? filterData.in_sort_number : INITIAL_SORT.number,
      in_sort_order: filterData.isSort ? filterData.sort_order : INITIAL_SORT.order,
      in_page_row_offset: pageData.skip,
      in_page_rows: pageData.take,
    });
  }, [baseParams, filterData, pageData, setAPICallingState]);

  const onClearFilter = React.useCallback(() => {
    setFilterField({ logic: "and", filters: [] });
    setFilterData((prev) => ({ ...prev, data: "", isFilter: false }));
    setAPICallingState(true);
    ActionRef.current.GetFilterQUery({
      ...baseParams,
      in_filter: "",
      in_sort_number: filterData.isSort ? filterData.in_sort_number : DEFAULT_SORT.number,
      in_sort_order: filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order,
      in_page_row_offset: pageData.skip,
      in_page_rows: pageData.take,
    });
  }, [baseParams, filterData, pageData, setAPICallingState]);

  if (!visible) return null;

  return (
    <>
      {initialLoading ? (
        <Window
          title={<ModalTitle />}
          onClose={onClosePopup}
          width={window.innerWidth * 0.8}
          height={window.innerHeight * 0.9}
          resizable={false}
          stage={stageChange}
          onStageChange={handleStageChange}
          modal={modal}
        >
          <div className="dtu-file-container-logs">
            <div className="content dtutitlebar">
              <div className="Quick-links eventtypequicklinks-dtu-Logs">
                <HeaderPage pageProps={{}} headerConfig={headerConfig} />
              </div>
            </div>

            <DataTable
              isAPICalling={isAPICalling}
              initialLoading={initialLoading}
              data={data}
              handleColumnMenu={handleColumnMenu}
              itemChange={() => {}}
              pageData={pageData}
              dataCount={dataCount}
              customColumn={UseCol}
              columns={columns}
              width={"auto"}
              className={"DataTable-dtuLog"}
              defaultPageSize={defaultPageSize}
              pageSize={pageSize}
              setPageSize={setPageSize}
              handlePageChange={handlePageChange}
              refreshClick={onRefreshClick}
              noDataFound={noDataFound}
              onClearFilter={onClearFilter}
              reorderable={false}
              resetSort={resetSort}
              initialFilter={filterField}
              initialSort={sortField}
              stageChange={stageChange}
              gridHeight={gridHeight}
            />
          </div>

          <WindowActionsBar layout={"end"}>
            <Button className="cancelbtn" onClick={onClosePopup} autoFocus={true}>
              Close
            </Button>
          </WindowActionsBar>
        </Window>
      ) : initialLoading && data.length === 0 ? (
        false
      ) : (
        <LoadingPanel />
      )}
    </>
  );
};

export default SavedQueryGrid;
